import React, { useState, useEffect, useRef } from 'react';
import { ErrorMessage } from 'pages/CreateVoucher/CreateVoucher.styles';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';
import { Row } from 'components/shared/Layout';
import TextInput from 'components/ui/TextInput';
import Select from 'components/ui/Select';
import currencySymbols from 'constants/currencies';
import {
  StyledRow,
  StyledColumn,
  StyledLabel,
  StyledTitle,
  StyledCountriesError,
  StyledCountriesController,
  StyledCountries,
  StyledMessage,
  StyledCountriesMessage
} from 'pages/CreateVoucher/CreateVoucher.styles';
import {
  VOUCHER_TYPE,
  VOUCHER_DESCRIPTION,
  TRIP_RESTRICTIONS,
  COUNTRIES_RESTRICTIONS,
  threeMonthsFromNow,
  calculateSalesPeriodEnd,
  calculateTravelPeriodStart,
  calculateTravelPeriodEnd,
  carriers,
  classOfServices,
  DE_COUNTRY,
  NL_COUNTRY,
  INCLUDE_ES_ER
} from '../utils/constant';
import Checkbox from 'components/ui/Checkbox';
import { FormDate, StyledDateField, StyledCalendar, StyledCalendarIcon } from './GenerateDiscountCodeForm.styles';

const GenerateDiscountCodeForm = ({ allowedCurrencies, defaultValues = {}, DEValue, NLValue, isValid, intl: { formatMessage } }) => {
  const [disabledESER, setDisabledESER] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { register, control, setValue, watch } = useFormContext();
  const salesDateStart = watch('salesDateStart');
  const calendarRef = useRef(null);

  const defaultSalesDate = defaultValues.salesDateStart || moment().format('YYYY-MM-DD');

  useEffect(() => {
    if (!salesDateStart) {
      setValue('salesDateStart', defaultSalesDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setCalendarOpen(false);
      }
    };

    if (calendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [calendarOpen]);

  const handleCalendarChange = (selectedDate) => {
    const isoDate = moment(selectedDate).format('YYYY-MM-DD');
    setValue('salesDateStart', isoDate, { shouldValidate: false });
    setCalendarOpen(false);
  };

  useEffect(() => {
    if (salesDateStart) {
      setValue('salesDateEnd', calculateSalesPeriodEnd(salesDateStart));
      setValue('travelDateStart', calculateTravelPeriodStart(salesDateStart));
      setValue('salesTravelsEnd', calculateTravelPeriodEnd(salesDateStart));
    }
  }, [salesDateStart, setValue]);

  const handleOnChange = ({ checked, name, onChange }) => {
    if (name === NL_COUNTRY || name === DE_COUNTRY) {
      if (checked === true) {
        setDisabledESER(false);
      } else {
        if (name === NL_COUNTRY) {
          setDisabledESER(!DEValue);
          if (!DEValue) setValue('ES_ER', false);
        } else {
          setDisabledESER(!NLValue);
          if (!NLValue) setValue('ES_ER', false);
        }
      }
    }

    onChange(checked);
  };

  return (
    <>
      <StyledTitle>
        <FormattedMessage id="discountCodes.discountCodeDetails" />
      </StyledTitle>
      <StyledRow columns={12}>
        <Row columns={12}>
          <StyledColumn alignItems="flex-start" flexDirection="column" span={3}>
            <StyledLabel htmlFor="voucherType">
              <FormattedMessage id="createVoucher.fields.voucherType" />
            </StyledLabel>
            <Select
              {...register('voucherType', { required: true })}
              id="voucherType"
              data-testid="voucherType"
              data-cy="voucher-type"
              defaultValue={VOUCHER_TYPE.SILVER_DISCOUNT}
            >
              <option value={VOUCHER_TYPE.SILVER_DISCOUNT}>{VOUCHER_DESCRIPTION.SILVER_DISCOUNT}</option>
            </Select>
          </StyledColumn>

          <StyledColumn alignItems="flex-start" flexDirection="column" span={3} start={5}>
            <>
              <StyledLabel htmlFor="currency">
                <FormattedMessage id="createVoucher.fields.currency" />
              </StyledLabel>
              <Select
                data-cy="currency"
                data-testid="currency"
                {...register('currency', { required: true })}
                id="currency"
                defaultValue={defaultValues.currency ?? allowedCurrencies[0]}
              >
                {Object.entries(currencySymbols)
                  .filter(([currencyType]) => allowedCurrencies.includes(currencyType))
                  .map(([currencyType, symbol]) => (
                    <option key={currencyType} value={currencyType}>
                      {currencyType} - {symbol}
                    </option>
                  ))}
              </Select>
            </>
          </StyledColumn>

          <StyledColumn alignItems="flex-start" flexDirection="column" span={3} start={9}>
            <StyledLabel htmlFor="amount">
              <FormattedMessage id="generateDiscountCode.maxPax" />
            </StyledLabel>
            <TextInput
              data-cy="number-of-paxs"
              type="number"
              data-testid="numberPaxs"
              width="100%"
              {...register('numberPaxs', {
                required: true,
                min: 1,
                max: 9
              })}
              min={1}
              max={9}
              id="numberPaxs"
              step="1"
              placeholder="0"
              defaultValue={defaultValues.numberPaxs}
            />
          </StyledColumn>
        </Row>

        <Row columns={12}>
          <StyledColumn alignItems="flex-start" flexDirection="column" span={3}>
            <StyledLabel htmlFor="salesDateStart">
              <FormattedMessage id="generateDiscountCode.salesDateStart" />
            </StyledLabel>
            <FormDate ref={calendarRef}>
              {/* Hidden input for form registration in YYYY-MM-DD format */}
              <input type="hidden" {...register('salesDateStart', { required: true })} value={salesDateStart || ''} />

              <StyledDateField data-cy="sales-date-field" onClick={() => setCalendarOpen(!calendarOpen)}>
                <TextInput
                  data-testid="salesDateStart"
                  data-cy="sales-date-start"
                  type="text"
                  readOnly
                  id="salesDateStart"
                  value={salesDateStart && moment(salesDateStart).isValid() ? moment(salesDateStart).format('DD/MM/YYYY') : ''}
                  placeholder="DD/MM/YYYY"
                  width="100%"
                />
                <StyledCalendarIcon size={20} data-cy="calendar-icon" />
              </StyledDateField>
              {calendarOpen && (
                <StyledCalendar
                  showNeighboringMonth={false}
                  tabIndex="-1"
                  value={salesDateStart && moment(salesDateStart).isValid() ? moment(salesDateStart).toDate() : moment().toDate()}
                  onChange={handleCalendarChange}
                  minDate={moment().toDate()}
                  maxDate={moment(threeMonthsFromNow).toDate()}
                />
              )}
            </FormDate>
          </StyledColumn>

          <StyledColumn alignItems="flex-start" flexDirection="column" span={3} start={5}>
            <StyledLabel htmlFor="salesDateEnd">
              <FormattedMessage id="createVoucher.fields.salesDateEnd" />
            </StyledLabel>
            <TextInput
              data-testid="salesDateEnd"
              data-cy="sales-date-end"
              type="text"
              width="100%"
              id="salesDateEnd"
              readOnly
              value={salesDateStart ? moment(calculateSalesPeriodEnd(salesDateStart)).format('DD/MM/YYYY') : ''}
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </StyledColumn>

          <StyledColumn alignItems="flex-start" flexDirection="column" span={3} start={9}>
            <StyledLabel htmlFor="tripRestrictions">
              <FormattedMessage id="generateDiscountCode.journeyType" />
            </StyledLabel>
            <Select
              data-testid="tripRestrictions"
              data-cy="trips-restrictions"
              width="100%"
              {...register('tripRestrictions', { required: true })}
              id="tripRestrictions"
              defaultValue={defaultValues.tripRestrictions}
            >
              {TRIP_RESTRICTIONS.map(({ name, description }, index) => (
                <option key={`${name}--${index}`} value={name}>
                  {formatMessage({ id: `generateDiscountCode.${description}` })}
                </option>
              ))}
            </Select>
          </StyledColumn>
        </Row>

        <Row columns={12}>
          <StyledColumn alignItems="flex-start" flexDirection="column" span={3}>
            <StyledLabel htmlFor="travelDateStart">
              <FormattedMessage id="generateDiscountCode.travelDateStart" />
            </StyledLabel>
            <TextInput
              data-testid="travelDateStart"
              data-cy="travel-date-start"
              type="text"
              width="100%"
              id="travelDateStart"
              readOnly
              value={salesDateStart ? moment(calculateTravelPeriodStart(salesDateStart)).format('DD/MM/YYYY') : ''}
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </StyledColumn>

          <StyledColumn alignItems="flex-start" flexDirection="column" span={3} start={5}>
            <StyledLabel htmlFor="salesTravelsEnd">
              <FormattedMessage id="generateDiscountCode.travelDateEnd" />
            </StyledLabel>
            <TextInput
              data-testid="salesTravelsEnd"
              data-cy="sales-travels-end"
              type="text"
              width="100%"
              id="salesTravelsEnd"
              readOnly
              value={salesDateStart ? moment(calculateTravelPeriodEnd(salesDateStart)).format('DD/MM/YYYY') : ''}
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </StyledColumn>
        </Row>

        <Row columns={12}>
          <StyledColumn alignItems="flex-start" flexDirection="column" span={3}>
            <StyledLabel htmlFor="carrier">
              <FormattedMessage id="generateDiscountCode.carriers" />
            </StyledLabel>
            <Select
              data-testid="carrier"
              width="100%"
              data-cy="carrier"
              {...register('carrier', { required: false })}
              id="carrier"
              defaultValue={defaultValues.carrier || carriers[0].description}
            >
              {carriers.map(({ name, description }) => (
                <option key={`${name}--${description}`} value={name}>
                  {formatMessage({ id: description })}
                </option>
              ))}
            </Select>
          </StyledColumn>

          <StyledColumn alignItems="flex-start" flexDirection="column" span={3} start={5}>
            <StyledLabel htmlFor="classOfService">
              <FormattedMessage id="generateDiscountCode.cos" />
            </StyledLabel>
            <Select
              data-testid="classOfService"
              width="100%"
              data-cy="classOfService"
              {...register('classOfService', { required: false })}
              id="classOfService"
              defaultValue={defaultValues.classOfService ?? classOfServices[0].description}
            >
              {classOfServices.map(({ name, description }) => (
                <option key={`${name}--${description}`} value={name}>
                  {description}
                </option>
              ))}
            </Select>
          </StyledColumn>

          <StyledColumn alignItems="flex-start" flexDirection="column" span={4} start={9}>
            <StyledLabel htmlFor="countriesRestriction">
              <StyledCountriesMessage>
                <FormattedMessage id="generateDiscountCode.permittedCountries" />
                {!isValid && (
                  <StyledCountriesError data-cy="countries-errors">
                    <ErrorMessage>
                      (<FormattedMessage id="generateDiscountCode.countriesError" />)
                    </ErrorMessage>
                  </StyledCountriesError>
                )}
              </StyledCountriesMessage>
            </StyledLabel>

            <StyledCountriesController>
              {COUNTRIES_RESTRICTIONS.map((item, index) => (
                <Controller
                  name={item}
                  key={`${item}--${index}`}
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <StyledCountries>
                      <StyledMessage>
                        <FormattedMessage id={`generateDiscountCode.${item}`} />
                      </StyledMessage>
                      <Checkbox
                        dataCy={`${item}`}
                        name={name}
                        checked={value}
                        disabled={name === INCLUDE_ES_ER ? disabledESER : false}
                        onChange={(checked) => handleOnChange({ checked, name, onChange })}
                      />
                    </StyledCountries>
                  )}
                />
              ))}
            </StyledCountriesController>
          </StyledColumn>
        </Row>
      </StyledRow>
    </>
  );
};

GenerateDiscountCodeForm.propTypes = {
  intl: intlShape,
  DEValue: PropTypes.bool,
  NLValue: PropTypes.bool,
  isValid: PropTypes.bool,
  allowedCurrencies: PropTypes.arrayOf(PropTypes.string),
  defaultValues: PropTypes.shape({
    currency: PropTypes.string,
    salesDateStart: PropTypes.string,
    salesDateEnd: PropTypes.string,
    travelDateStart: PropTypes.string,
    salesTravelsEnd: PropTypes.string,
    numberPaxs: PropTypes.number,
    carrier: PropTypes.string,
    classOfService: PropTypes.string
  })
};

export default injectIntl(GenerateDiscountCodeForm);
